import { Modal } from '@thanx/uikit/modal'
import { alert } from 'actions/flash'
import icCopy from 'assets/images/ic-copy.svg'
import { buildTranslate } from 'locales'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useStyletron } from 'styletron-react'

type PropsT = {
  isOpen: boolean
  onClose: () => void
  hashid: string
}

const t = buildTranslate('redeem_manager.detail')

const RewardIdModal: React.FC<PropsT> = ({ isOpen, onClose, hashid }) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()

  const copyRewardIdToClipboard = async () => {
    if (!hashid) return

    await navigator.clipboard.writeText(hashid)
    dispatch(
      alert({
        key: 'success',
        message: t('reward_id_copied_to_clipboard'),
        timeout: 5,
      })
    )
  }

  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="auto" position="center">
      <div
        className={css({
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0px 4px 8px rgba(54, 66, 78, 0.3)',
          padding: '48px',
          position: 'relative',
          width: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        })}
      >
        <button
          type="button"
          onClick={onClose}
          className={css({
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '40px',
            height: '40px',
            backgroundColor: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            color: '#6a747f',
            fontWeight: 'normal',
            lineHeight: 1,
            ':hover': {
              backgroundColor: '#f8f9fa',
              color: '#36424e',
            },
          })}
        >
          ×
        </button>

        {/* Title section */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <h2
            className={css({
              fontFamily: 'Lato',
              fontWeight: 'bold',
              fontSize: '24px',
              lineHeight: '28px',
              letterSpacing: '0.25px',
              color: '#36424e',
              margin: 0,
            })}
          >
            {t('reward_id_modal_title')}
          </h2>
          <p
            className={css({
              fontFamily: 'Lato',
              fontWeight: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#6a747f',
              margin: 0,
            })}
          >
            {t('reward_id_modal_subtitle')}
          </p>
        </div>

        {/* Notification banner */}
        <div
          className={css({
            backgroundColor: '#e5f0f8',
            padding: '24px',
            borderRadius: '4px',
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
          })}
        >
          <div
            className={css({
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            })}
          >
            <div
              className={css({
                width: '20px',
                height: '20px',
                backgroundColor: '#0073bf',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '12px',
              })}
            >
              i
            </div>
          </div>
          <p
            className={css({
              fontFamily: 'Lato',
              fontWeight: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#6a747f',
              margin: 0,
              flex: 1,
            })}
          >
            {t('reward_restrictions_disclaimer')}
          </p>
        </div>

        {/* Reward ID field */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <label
            className={css({
              fontFamily: 'Lato',
              fontWeight: 'bold',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#36424e',
            })}
          >
            {t('reward_id_modal_title')}
          </label>
          <div
            className={css({
              backgroundColor: 'white',
              border: '1px solid #c2c8ce',
              borderRadius: '4px',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
          >
            <span
              className={css({
                fontFamily: 'Lato',
                fontWeight: 'normal',
                fontSize: '16px',
                lineHeight: '24px',
                letterSpacing: '0.25px',
                color: '#36424e',
                wordBreak: 'break-all',
              })}
            >
              {hashid || t('no_id_available')}
            </span>
            <button
              type="button"
              onClick={copyRewardIdToClipboard}
              disabled={!hashid}
              className={css({
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '8px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: '4px',
                cursor: hashid ? 'pointer' : 'not-allowed',
                opacity: hashid ? 1 : 0.5,
                ':hover': hashid
                  ? {
                      backgroundColor: '#f1fbfb',
                    }
                  : {},
              })}
            >
              <img src={icCopy} alt="Copy" width={24} height={24} />
              <span
                className={css({
                  fontFamily: 'Lato',
                  fontWeight: '700',
                  fontSize: '16px',
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  textAlign: 'center',
                  color: '#0073bf',
                })}
              >
                {t('copy')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default RewardIdModal
